import { useEffect, useRef, useState, type ReactNode } from 'react';
import { config } from '../api/config';
import { Avatar } from '../st8n-tickets/kit.jsx';
import { Brand } from './brand';

export type BarTheme = 'system' | 'light' | 'dark';

/**
 * Deleg8's own header: the mark, the company's name (portal), and an account menu. Used when st8n is off (no "Sign in with
 * st8n", no st8n apps grid) and for the customer portal, which never shows st8n anything. Sticky, safe-area aware, 44px targets.
 */
export function Deleg8Bar({ account, name, home = '/', onHome, links = [], theme, onTheme, onSignOut, onSignIn, left }: {
  account: { name: string | null; email: string | null } | null;
  /** A company name to show beside the mark (the portal), else the mark alone. */
  name?: string;
  home?: string;
  onHome?: () => void;
  links?: { label: string; href: string; go: () => void }[];
  theme?: BarTheme;
  onTheme?: (t: BarTheme) => void;
  onSignOut?: () => void;
  /** Signed out: shows "Sign in with st8n" (only pass it when st8n sign-in is on). */
  onSignIn?: () => void;
  left?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menuItems = () => [...(box.current?.querySelectorAll<HTMLElement>('[role=menuitem], [role=menuitemradio]') ?? [])];
  useEffect(() => {
    if (!open) return;
    menuItems()[0]?.focus({ preventScroll: true });
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); button.current?.focus(); } };
    const c = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    addEventListener('keydown', k);
    addEventListener('mousedown', c);
    return () => { removeEventListener('keydown', k); removeEventListener('mousedown', c); };
  }, [open]);
  const brand = config.brandName;
  return (
    <header className="safe-t sticky top-0 z-30 border-b border-line bg-bg/95 backdrop-blur" data-testid="deleg8-bar">
      <div className="flex h-14 items-center gap-2 px-4 lg:px-6">
        {left}
        <a href={home} onClick={(e) => { if (onHome) { e.preventDefault(); onHome(); } }} className="flex min-h-11 min-w-0 items-center gap-2.5" aria-label={`${name ?? brand ?? 'Deleg8'} home`}>
          {name || brand ? <Brand size={28} mark /> : <Brand size={28} />}
          {name || brand ? <span className="truncate text-[16px] font-semibold tracking-tight">{name ?? brand}</span> : null}
        </a>
        <span className="ml-auto" />
        {account ? (
          <div className="relative" ref={box}>
            <button ref={button} type="button" className="flex h-11 w-11 items-center justify-center rounded-full" aria-label={`Account: ${account.name || account.email || ''}`} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)} data-testid="bar-account">
              <Avatar person={{ id: account.email ?? account.name ?? '?', name: account.name, email: account.email }} />
            </button>
            {open ? (
              <div role="menu" aria-label="Account" onKeyDown={(e) => {
                if (e.key === 'Tab') { setOpen(false); return; }
                const all = menuItems();
                const at = all.indexOf(document.activeElement as HTMLElement);
                const to = e.key === 'ArrowDown' ? (at + 1) % all.length : e.key === 'ArrowUp' ? (at - 1 + all.length) % all.length : e.key === 'Home' ? 0 : e.key === 'End' ? all.length - 1 : -1;
                if (to < 0 || !all.length) return;
                e.preventDefault();
                all[to]!.focus();
              }} className="animate-fade absolute top-12 right-0 z-40 w-[min(300px,calc(100vw-24px))] rounded-2xl border border-line bg-panel p-2 shadow-2" data-testid="bar-menu">
                <div className="px-3 py-2"><b className="block truncate text-[15px]">{account.name ?? account.email}</b>{account.name && account.email ? <span className="block truncate text-[13px] text-dim">{account.email}</span> : null}</div>
                {links.map((l) => <a key={l.href} role="menuitem" href={l.href} className="flex min-h-11 items-center rounded-xl px-3 text-[15px] hover:bg-panel-2" onClick={(e) => { e.preventDefault(); setOpen(false); l.go(); }}>{l.label}</a>)}
                {theme && onTheme ? (
                  <div className="px-3 py-2" role="group" aria-label="Theme">
                    <div className="flex rounded-xl bg-panel-2 p-0.5">
                      {(['system', 'light', 'dark'] as const).map((t) => <button key={t} type="button" role="menuitemradio" aria-checked={theme === t} className={`h-11 flex-1 rounded-[10px] text-[13px] ${theme === t ? 'bg-panel font-semibold shadow-1' : 'text-dim'}`} onClick={() => onTheme(t)}>{t === 'system' ? 'Auto' : t === 'light' ? 'Light' : 'Dark'}</button>)}
                    </div>
                  </div>
                ) : null}
                {onSignOut ? <button type="button" role="menuitem" className="flex min-h-11 w-full items-center rounded-xl px-3 text-left text-[15px] hover:bg-panel-2" onClick={() => { setOpen(false); onSignOut(); }} data-testid="bar-signout">Sign out</button> : null}
              </div>
            ) : null}
          </div>
        ) : onSignIn ? <button type="button" className="btn-line btn-sm" onClick={onSignIn} data-testid="bar-signin">Sign in with st8n</button> : null}
      </div>
    </header>
  );
}
